// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import {
	oemMap,
	ORDER_CREATION_TYPES,
	PAYMENT_TYPES,
	OEM_KEY_TO_HOTJAR_SITE_ID,
	STACKING_BREAKPOINT,
	PAYMENT_CHOICES,
	Services,
	TRADE_IN_OPTIONS,
} from '@ekhodealer/ekho-common';
import {
	getOemId,
	getPaymentConfigOptions,
	vehicleNeedsVin,
	hashEmail,
	secureSessionStorage,
} from '@ekhodealer/ekho-common/utils';
import { TourProvider } from '@reactour/tour';
import Hotjar from '@hotjar/browser';
import { setupSardine, updateSardineConfig } from '@sardine-ai/web-sdk';

import { onAuthStateChanged } from 'firebase/auth';
import { ThemeContext, Portal, useDarkMode, getOS } from '@ekhodealer/ekho-common/components';
import ContextWrapper from '../contexts/contextWrapper';
import steps, { styles } from '../steps';
import {
	COLORS,
	DELIVERY_CHOICES,
	STRIPE_PAYMENT_METHOD_TYPES,
	PAYMENT_DESCRIPTION_TYPES,
	ENV,
	MODULES,
	KLARNA_CHECKOUT_MIN,
	KLARNA_CHECKOUT_MAX,
	DISCOUNT_CATEGORIES,
} from '../common/data/constants';
import { dashboardMenu, demoPages, layoutMenu } from '../menu';
// eslint-disable-next-line camelcase
import { track_checkout_buyer_abandoned_cart } from '../Utils/analyticsFuncs';
import { isValidEmail } from '../Utils/validationFuncs';
import { auth } from '../firebase';
import {
	getUnauthenticatedOemData,
	createCheckoutPaymentIntent,
	getCheckoutCart,
	createLog,
	getOemVehicles,
	getOemPickUpLocations,
	checkoutCartWasOpened,
	getStateTaxAndTARData,
	fetchGoogleMapsApiKey,
	getVehicleConfig,
	getServicesProvider,
	createSession,
	getServicesProviders,
} from '../serverCalls';
import {
	debounceSimple,
	getAndSetRelevantFinancingPrograms,
	getClientIdFromUrl,
	getSessionIdFromUrl,
	getVariantOptionsFromVehicles,
	isPurchasePreorder,
	isDiscountLive,
	idQualifyForDiscount,
} from '../Utils/util_funcs';
import {
	fetchModelYearVehicles,
	generateSmartConfiguration,
	isVehicleConfigurationValid,
} from '../components/vehicleConfiguratorComponents/Utils';
import AppRouter from './AppRouter';

const SARDINE_CONFIG =
	process.env.REACT_APP_CURR_ENV === ENV.PRODUCTION
		? {
				clientId: 'c54e4c8a-ac89-4531-b4ad-3ff85f39a6b8',
				environment: 'production',
		  }
		: {
				clientId: 'edc12a66-2e28-486f-bf32-c7063511c810',
				environment: 'sandbox',
		  };

const siteId = OEM_KEY_TO_HOTJAR_SITE_ID.CHECKOUT[process.env.REACT_APP_OEM_KEY];
const shouldInitHotjar = process.env.REACT_APP_CURR_ENV === ENV.PRODUCTION && siteId !== undefined;

const updateUrlWithNewProductId = (newProductId) => {
	const url = new URL(window.location.href);
	url.searchParams.set('productId', newProductId);
	window.history.pushState({}, '', url);
};

const App = () => {
	getOS();

	// ================ DEFINE CONTEXTS ================ //

	// Session Context
	const [session, setSession] = useState(null);
	const sessionValue = useMemo(() => ({ session, setSession }), [session]);

	// Active Module Context
	const [activeModule, setActiveModule] = useState(null);
	const activeModuleValue = useMemo(() => ({ activeModule, setActiveModule }), [activeModule]);

	// Fetch Checkout Cart Context
	const [isLoading, setIsLoading] = useState(false);
	const fetchCheckoutValue = useMemo(
		() => ({ fetchCheckoutData, isLoading }),
		[isLoading], // add any other dependencies if fetchCheckoutData uses any other state
	);

	// Vehicle Config Context
	const [vehicleConfig, setVehicleConfig] = useState(null);
	const vehicleConfigValue = useMemo(
		() => ({ vehicleConfig, setVehicleConfig }),
		[vehicleConfig],
	);

	// Current Vehicle Config Context
	const [currentVehicleConfig, setCurrentVehicleConfig] = useState(null);
	const currentVehicleConfigValue = useMemo(
		() => ({ currentVehicleConfig, setCurrentVehicleConfig }),
		[currentVehicleConfig],
	);

	// paymentIntent context
	const [currentPaymentIntent, setCurrentPaymentIntent] = useState(null);
	const currentPaymentIntentValue = useMemo(
		() => ({ currentPaymentIntent, setCurrentPaymentIntent }),
		[currentPaymentIntent],
	);

	// Google maps api key context
	const [googleMapsApiKey, setGoogleMapsApiKey] = useState(null);
	const googleMapsApiKeyValue = useMemo(
		() => ({ googleMapsApiKey, setGoogleMapsApiKey }),
		[googleMapsApiKey],
	);
	const [registeredUserProps, setRegisteredUser] = useState(null);
	const registeredUserValue = useMemo(
		() => ({ registeredUserProps, setRegisteredUser }),
		[registeredUserProps],
	);

	const [selectedVariantOptions, setSelectedVariantOptions] = useState(null);
	const selectedVariantOptionsValue = useMemo(
		() => ({ selectedVariantOptions, setSelectedVariantOptions }),
		[selectedVariantOptions],
	);

	// vehicle context
	const [vehicles, setVehicles] = useState([]);
	const vehiclesValue = useMemo(() => ({ vehicles, setVehicles }), [vehicles]);

	// variant images context
	const [variantImages, setVariantImages] = useState(new Map());
	const variantImagesValue = useMemo(
		() => ({ variantImages, setVariantImages }),
		[variantImages],
	);

	// unique accessories context
	const [uniqueAccessories, setUniqueAccessories] = useState(null);
	const uniqueAccessoriesValue = useMemo(
		() => ({ uniqueAccessories, setUniqueAccessories }),
		[uniqueAccessories],
	);

	// loose accessories context
	const [looseAccessories, setLooseAccessories] = useState(null);
	const looseAccessoriesValue = useMemo(
		() => ({ looseAccessories, setLooseAccessories }),
		[looseAccessories],
	);

	// accessory count context
	const [accessoryCounts, setAccessoryCounts] = useState(null);
	const accessoryCountsValue = useMemo(
		() => ({ accessoryCounts, setAccessoryCounts }),
		[accessoryCounts],
	);

	// misc ecomm data context
	const [checkoutCart, setCheckoutCart] = useState(null);
	const checkoutCartValue = useMemo(() => ({ checkoutCart, setCheckoutCart }), [checkoutCart]);
	// State for whether or not the user has bolt ons
	const [cartHasBoltOns, setCartHasBoltOns] = useState(null);
	const cartHasBoltOnsValue = useMemo(
		() => ({ cartHasBoltOns, setCartHasBoltOns }),
		[cartHasBoltOns],
	);

	// OEM vehicles context
	const [oemVehicles, setOemVehicles] = useState(null);
	const oemVehiclesValue = useMemo(() => ({ oemVehicles, setOemVehicles }), [oemVehicles]);

	// non blocking OEM pick up locations context
	const [oemPickUpLocations, setOemPickUpLocations] = useState(null);
	const oemPickUpLocationsValue = useMemo(
		() => ({ oemPickUpLocations, setOemPickUpLocations }),
		[oemPickUpLocations],
	);

	const [paymentInvoice, setPaymentInvoice] = useState(null);
	const paymentInvoiceValue = useMemo(
		() => ({ paymentInvoice, setPaymentInvoice }),
		[paymentInvoice],
	);

	const [paymentInvoiceTotals, setPaymentInvoiceTotals] = useState(null);
	const paymentInvoiceTotalsValue = useMemo(
		() => ({ paymentInvoiceTotals, setPaymentInvoiceTotals }),
		[paymentInvoiceTotals],
	);

	// for brand styling context
	const [oemProfileProps, setOemProfile] = useState(null);
	const oemProfileValue = useMemo(() => ({ oemProfileProps, setOemProfile }), [oemProfileProps]);

	// stacked context
	const [stacked, setStacked] = useState(window.innerWidth < STACKING_BREAKPOINT);
	const stackedValue = useMemo(() => ({ stacked, setStacked }), [stacked]);

	// Selected payment option context
	const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
	const selectedPaymentOptionValue = useMemo(
		() => ({ selectedPaymentOption, setSelectedPaymentOption }),
		[selectedPaymentOption],
	);

	// selected trade in option context
	const [selectedTradeInOption, setSelectedTradeInOption] = useState(
		TRADE_IN_OPTIONS.NO_TRADE_IN,
	);
	const selectedTradeInOptionValue = useMemo(
		() => ({ selectedTradeInOption, setSelectedTradeInOption }),
		[selectedTradeInOption],
	);

	// Selected delivery option context
	const [selectedDeliveryOption, setSelectedDeliveryOption] = useState(null);
	const selectedDeliveryOptionValue = useMemo(
		() => ({ selectedDeliveryOption, setSelectedDeliveryOption }),
		[selectedDeliveryOption],
	);

	// selected registration option context
	const [selectedRegistrationOption, setSelectedRegistrationOption] = useState(null);
	const selectedRegistrationOptionValue = useMemo(
		() => ({ selectedRegistrationOption, setSelectedRegistrationOption }),
		[selectedRegistrationOption],
	);

	// checkout fired (random)
	const [checkoutFired, setCheckoutFired] = useState(0);

	// registrationAddress context
	const [registrationAddress, setRegistrationAddress] = useState({
		addressString: '',
		addressLineOne: '',
		addressLineTwo: '',
		city: '',
		county: '',
		state: '',
		zip: '',
	});
	const registrationAddressValue = useMemo(
		() => ({ registrationAddress, setRegistrationAddress }),
		[registrationAddress],
	);

	// account details context
	const [accountDetails, setAccountDetails] = useState({
		fullName: '',
		emailAddress: '',
		phoneNumber: '',
	});
	const accountDetailsValue = useMemo(
		() => ({ accountDetails, setAccountDetails }),
		[accountDetails],
	);

	// payment intents context
	const [paymentIntents, setPaymentIntents] = useState(null);
	const paymentIntentsValue = useMemo(
		() => ({ paymentIntents, setPaymentIntents }),
		[paymentIntents],
	);

	// state data context (will only have taxes for now)
	const [stateData, setStateData] = useState(null);
	const stateDataValue = useMemo(() => ({ stateData, setStateData }), [stateData]);

	// relevant financing programs context
	const [relevantFinancingPrograms, setRelevantFinancingPrograms] = useState(null);
	const relevantFinancingProgramsValue = useMemo(
		() => ({ relevantFinancingPrograms, setRelevantFinancingPrograms }),
		[relevantFinancingPrograms],
	);

	// discounts context
	const [activeDiscounts, setActiveDiscounts] = useState([]);
	const [applicableDiscounts, setApplicableDiscounts] = useState([]);
	const activeDiscountsValue = useMemo(
		() => ({
			activeDiscounts,
			setActiveDiscounts,
			applicableDiscounts,
			setApplicableDiscounts,
		}),
		[activeDiscounts, applicableDiscounts],
	);

	const [orderIsBeingPlaced, setOrderIsBeingPlaced] = useState(false);
	const orderIsBeingPlacedValue = useMemo(
		() => ({
			orderIsBeingPlaced,
			setOrderIsBeingPlaced,
		}),
		[orderIsBeingPlaced],
	);

	const [abandonedCartId, setAbandonedCartId] = useState(null);
	const abandonedCartIdValue = useMemo(
		() => ({
			abandonedCartId,
			setAbandonedCartId,
		}),
		[abandonedCartId],
	);

	// Delivery Estimate String
	const [deliveryTime, setDeliveryTime] = useState('Loading...');
	const deliveryTimeValue = useMemo(() => ({ deliveryTime, setDeliveryTime }), [deliveryTime]);

	const [shippingAddressSameAsRegistrationAddress, setShippingAddressSameAsRegistrationAddress] =
		useState(false);
	const shippingAddressSameAsRegistrationAddressValue = useMemo(
		() => ({
			shippingAddressSameAsRegistrationAddress,
			setShippingAddressSameAsRegistrationAddress,
		}),
		[shippingAddressSameAsRegistrationAddress],
	);

	const [fetchedServicesProviders, setFetchedServicesProviders] = useState([]);
	const fetchedServicesProvidersValue = useMemo(
		() => ({ fetchedServicesProviders, setFetchedServicesProviders }),
		[fetchedServicesProviders],
	);

	// Add this to your App component
	const [lastActivityTime, setLastActivityTime] = useState(Date.now());
	const [inactivityTimer, setInactivityTimer] = useState(null);

	const [servicesProviders, setServicesProviders] = useState({});

	const updateServicesProviders = (key, provider) => {
		setServicesProviders((prev) => ({ ...prev, [key]: provider }));
	};

	const servicesProvidersValue = useMemo(
		() => ({
			servicesProviders,
			updateServicesProviders,
		}),
		[servicesProviders],
	);

	const prevSaleProviderRef = useRef();

	const modelYearSearchParams = new URLSearchParams(window.location.search);
	const [productId, setProductId] = useState(modelYearSearchParams.get('productId'));
	const [currentModelYear, setCurrentModelYear] = useState(null);

	// Model year vehicles state
	const [modelYearVehicles, setModelYearVehicles] = useState(null);
	const [modelYearStockStatus, setModelYearStockStatus] = useState(null);
	const [isLoadingModelYearStock, setIsLoadingModelYearStock] = useState(false);

	// Disable checkout when all model years are out of stock
	const [disableCheckout, setDisableCheckout] = useState(false);

	useEffect(() => {
		if (productId) {
			const yearFromProductId = productId.slice(-4);
			if (yearFromProductId !== currentModelYear) {
				setCurrentModelYear(yearFromProductId);
			}
		}
	}, [productId]);

	const handleModelYearChange = async (newYear) => {
		if (!newYear || newYear === currentModelYear) return;

		const newProductId = productId.slice(0, -4) + newYear;
		updateUrlWithNewProductId(newProductId);
		setCurrentModelYear(newYear);
		setActiveDiscounts([]);
		setApplicableDiscounts([]);
		await fetchVehicleConfig(newProductId);
	};

	const allModelYears = useMemo(() => {
		if (!productId || !oemProfileProps) return null;

		return oemProfileProps.vehicleProductIds.filter(
			(id) => id.slice(0, -4) === productId.slice(0, -4),
		);
	}, [productId, oemProfileProps]);

	const isModelYearSelectionEnabled = useMemo(() => {
		return (
			oemProfileProps?.isModelYearSelectionEnabled &&
			allModelYears &&
			allModelYears.length > 1
		);
	}, [oemProfileProps?.isModelYearSelectionEnabled, allModelYears]);

	const modelYearSelectionProviderValue = useMemo(
		() => ({
			currentModelYear,
			setCurrentModelYear,
			allModelYears,
			handleModelYearChange,
			isModelYearSelectionEnabled,
			// Model year vehicles data
			modelYearVehicles,
			setModelYearVehicles,
			modelYearStockStatus,
			setModelYearStockStatus,
			isLoadingModelYearStock,
			setIsLoadingModelYearStock,
			// Disable checkout when all model years are out of stock
			disableCheckout,
			setDisableCheckout,
		}),
		[
			currentModelYear,
			allModelYears,
			isModelYearSelectionEnabled,
			modelYearVehicles,
			modelYearStockStatus,
			isLoadingModelYearStock,
			disableCheckout,
		],
	);

	// Function to handle user activity
	const handleUserActivity = () => {
		setLastActivityTime(Date.now());
	};

	// Function to check if user has been inactive
	const checkUserInactivity = async () => {
		const currentTime = Date.now();
		const inactiveTime = currentTime - lastActivityTime;
		const thirtyMinutesInMs = 30 * 60 * 1000;

		if (inactiveTime >= thirtyMinutesInMs) {
			// User has been inactive for 30+ minutes, refresh the session
			createLog(
				'session-refresh-inactivity',
				'Refreshing session after 30 minutes of inactivity',
				'INFO',
				{ checkoutCartId: checkoutCart?.id },
			);
			await getAndSetSession();
		}
	};

	// Function to update Sardine configuration with session data
	const updateSardineWithSessionData = async (updatedSession) => {
		// Only proceed if we have session key
		const { emailAddress } = accountDetails;
		const { sessionKey } = updatedSession;
		if (!sessionKey) {
			return;
		}

		// Create update object preserving all existing session properties
		const sardineUpdate = {
			...updatedSession,
			flow: 'checkout',
		};

		// Only add customerId if email exists
		if (emailAddress) {
			const customerId = await hashEmail(emailAddress);
			sardineUpdate.customerId = customerId;
		}

		// Update the session context
		setSession(sardineUpdate);

		// Save updated session with secure storage
		secureSessionStorage.saveSession(sardineUpdate);

		// Update the Sardine config
		updateSardineConfig(sardineUpdate);
	};

	// Set up activity tracking and inactivity timer
	useEffect(() => {
		// Track user activity events
		const activityEvents = [
			'mousemove',
			'mousedown',
			'keypress',
			'scroll',
			'touchstart',
			'click',
		];

		// Add event listeners for all activity events
		activityEvents.forEach((event) => {
			window.addEventListener(event, handleUserActivity);
		});

		// Set up interval to check for inactivity every minute
		const timer = setInterval(checkUserInactivity, 60 * 1000);
		setInactivityTimer(timer);

		// Clean up event listeners and timer on unmount
		return () => {
			activityEvents.forEach((event) => {
				window.removeEventListener(event, handleUserActivity);
			});

			if (inactivityTimer) {
				clearInterval(inactivityTimer);
			}
		};
	}, []);

	// END OF CONTEXTS
	// ================ USE EFFECTS NECESSARY PRIOR TO LOADING ================ //
	useEffect(() => {
		// Initialize Hotjar
		if (shouldInitHotjar) {
			const hotjarVersion = 6;
			Hotjar.init(siteId, hotjarVersion);
		}

		// Initialize Sardine SDK
		setupSardine(SARDINE_CONFIG);

		// Clear session on page load for security
		// This forces a fresh session on every page load
		secureSessionStorage.clearSession();

		// Install the reload handler to clear session
		const handlePageReload = () => {
			// This will run when the page is about to unload/refresh
			secureSessionStorage.clearSession();
		};

		// Add the event listeners for both page close and page reload
		window.addEventListener('unload', handlePageReload);

		// Get the session when the app loads
		getAndSetSession();

		return () => {
			window.removeEventListener('unload', handlePageReload);
		};
	}, []);

	// Conditionally load analytics (GA, TikTok Pixel, Meta Pixel) scripts if the environment variable is defined
	useEffect(() => {
		// Check if the environment variables for Google Tags are defined
		const gaTagManagerId = process.env.REACT_APP_GA_MANAGER_ID;
		const gaKey = process.env.REACT_APP_GA_KEY;
		const googleAdsKey = process.env.REACT_APP_GOOGLE_ADS_KEY;
		const WCInline = process.env.REACT_APP_WHATCONVERTS_INLINE;
		const WCExternal = process.env.REACT_APP_WHATCONVERTS_EXTERNAL;
		if (WCInline && WCExternal) {
			// Create and inject the inline script
			const inlineScript = document.createElement('script');
			inlineScript.textContent = WCInline;

			// Handle inline script error
			inlineScript.onerror = (e) => {
				console.log(`Error loading inline script: ${e}`);
			};

			document.head.appendChild(inlineScript);

			// Create and inject the external script
			const externalScript = document.createElement('script');
			externalScript.src = WCExternal;

			// Handle external script load error
			externalScript.onerror = (e) => {
				console.log(`Error loading external script: ${e}`);
			};

			document.head.appendChild(externalScript);
		}
		if (gaTagManagerId) {
			// Create a script tag for Google Tag Manager
			const scriptTag = document.createElement('script');
			scriptTag.async = true;
			scriptTag.src = `https://www.googletagmanager.com/gtm.js?id=${gaTagManagerId}`;

			// Append the script to the document head
			document.head.appendChild(scriptTag);

			// Initialize dataLayer and define gtag function globally
			window.dataLayer = window.dataLayer || [];
			window.gtag = function () {
				// eslint-disable-next-line no-undef, prefer-rest-params
				dataLayer.push(arguments);
			};

			// Handle script load
			scriptTag.onload = () => {
				window.gtag('js', new Date());

				// Only specify client_id if it's in the URL, otherwise let GA use its own
				const clientIdFromUrl = getClientIdFromUrl();
				const configOptions = {};

				if (clientIdFromUrl) {
					configOptions.client_id = clientIdFromUrl;
				}

				// Same for session_id
				const sessionIdFromUrl = getSessionIdFromUrl();
				if (sessionIdFromUrl) {
					configOptions.session_id = sessionIdFromUrl;
				}

				window.gtag('config', gaTagManagerId, configOptions);
				window.gtag('event', 'begin_checkout', {
					currency: 'USD',
				});
			};

			// Handle script load error
			scriptTag.onerror = (e) => {
				createLog(
					'gtag-load-error',
					`Error loading Google Tag Manager script: ${e}`,
					'ERROR',
					{
						checkoutCartId: checkoutCart.id,
						gaTagManagerId,
					},
				);
			};
		} else if (gaKey) {
			// Create a script tag for Google Analytics
			const scriptTag = document.createElement('script');
			scriptTag.async = true;
			scriptTag.src = `https://www.googletagmanager.com/gtag/js?id=${gaKey}`;
			document.head.appendChild(scriptTag);

			// Initialize dataLayer and define gtag function globally
			window.dataLayer = window.dataLayer || [];
			window.gtag = function () {
				// eslint-disable-next-line no-undef, prefer-rest-params
				dataLayer.push(arguments);
			};

			scriptTag.onload = () => {
				// Define the global gtag function only after the script is fully loaded
				window.gtag('js', new Date());
				window.gtag('config', gaKey, {
					client_id: getClientIdFromUrl(),
					session_id: getSessionIdFromUrl(),
				});
				// Configure Google Ads if it is defined
				if (googleAdsKey) {
					window.gtag('config', googleAdsKey);
				}
				window.gtag('event', 'begin_checkout', {
					currency: 'USD',
				});
			};
		}

		// Check if the TikTok Pixel id environment variable is defined
		const tiktokPixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID;
		if (tiktokPixelId) {
			// Create a script tag for TikTok Pixel
			const tiktokScriptTag = document.createElement('script');
			tiktokScriptTag.async = true;
			tiktokScriptTag.innerHTML = `
			  !function (w, d, t) {
				w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
				var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
				;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
				  ttq.load('${tiktokPixelId}');
				  ttq.page();
			  }(window, document, 'ttq');
			`;
			document.head.appendChild(tiktokScriptTag);
		}

		// Check if the Meta Pixel id environment variable is defined
		const metaPixelId = process.env.REACT_APP_META_PIXEL_ID;
		if (metaPixelId) {
			// Create a script tag for Meta Pixel
			const metaScriptTag = document.createElement('script');
			metaScriptTag.async = true;
			metaScriptTag.innerHTML = `
				!function(f,b,e,v,n,t,s) {
					if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					n.queue=[];t=b.createElement(e);t.async=!0;
					t.src=v;s=b.getElementsByTagName(e)[0];
					s.parentNode.insertBefore(t,s)}(window, document,'script',
					'https://connect.facebook.net/en_US/fbevents.js');
				fbq('init', '${metaPixelId}');
				fbq('track', 'PageView');`;

			// Append the script tag to the document head
			document.head.appendChild(metaScriptTag);

			// Create a noscript tag for Meta Pixel
			const metaScriptNoTag = document.createElement('noscript');
			metaScriptNoTag.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1"/>`;

			// Append the noscript tag to the document body
			document.body.appendChild(metaScriptNoTag);
		}
	}, []);

	// Listen for window resize events
	useEffect(() => {
		const debouncedHandleResize = debounceSimple(function handleResize() {
			setStacked(window.innerWidth < STACKING_BREAKPOINT);
		}, 100);
		window.addEventListener('resize', debouncedHandleResize);
		return () => {
			window.removeEventListener('resize', debouncedHandleResize);
		};
	});

	// Add this function to get or create a session and set it in the context
	async function getAndSetSession() {
		try {
			const result = await getSession();
			if (result) {
				updateSardineWithSessionData(result);
			}
		} catch (error) {
			createLog('getAndSetSession', `Error getting session: ${error}`, 'ERROR', {});
		}
	}

	// Function to get or create a Sardine session
	async function getSession() {
		// Check secure storage for an existing session
		const storedSession = secureSessionStorage.getSession();
		if (storedSession) {
			return storedSession;
		}

		// Otherwise, create a new session
		try {
			// Create a new session - this will include server-defined expiry
			const newSession = await createSession();
			if (newSession) {
				// Store the session with secure storage
				secureSessionStorage.saveSession(newSession);
				return newSession;
			}
			return null;
		} catch (error) {
			createLog('getSession', `Error creating session: ${error}`, 'ERROR', {});
			return null;
		}
	}

	async function getOem() {
		const { REACT_APP_OEM_KEY } = process.env;
		if (!(REACT_APP_OEM_KEY in oemMap)) {
			throw new Error('Error: no OEM with provided name!');
		}
		const oemId = getOemId(REACT_APP_OEM_KEY, process.env.REACT_APP_BRAND_KEY);
		const { oem } = await getUnauthenticatedOemData(oemId);
		setOemProfile(oem);
	}
	// run this async
	async function getAndSetOemVehicles(vehiclesArr) {
		// get all unique productIds from vehicles
		const productIds = new Set();
		vehiclesArr.forEach((vehicle) => {
			productIds.add(vehicle.productId);
		});

		// get all vehicles with those productIds using the getOemVehicles endpoint
		// get the oemId from the environment variables instead of from the checkoutCart
		const { REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY } = process.env;
		const oemId = getOemId(REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY);
		const { vehicles: fetchedOemVehicles } = await getOemVehicles(oemId, [...productIds]);
		setOemVehicles(fetchedOemVehicles);
	}
	// run this async
	async function getAndSetOemPickUpLocations() {
		// Only fetch pickup locations if pickup is enabled for this OEM
		if (oemProfileProps?.fulfillmentOptions?.pickup?.isEnabled) {
			const { REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY } = process.env;
			const oemId = getOemId(REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY);
			const { pickUpLocations } = await getOemPickUpLocations(oemId);
			setOemPickUpLocations(pickUpLocations);
		} else {
			// Set empty array when pickup is not enabled
			setOemPickUpLocations([]);
		}
	}
	// create async function to fetch state taxes and fees using the getStateTaxeAndTarData endpoint
	async function getAndSetStateData() {
		const stateDataObj = await getStateTaxAndTARData();
		setStateData(stateDataObj);
	}
	async function getGoogleMapsApiKey() {
		const { key } = await fetchGoogleMapsApiKey();
		setGoogleMapsApiKey(key);
	}
	async function getAndSetSellingServicesProvider(providerId) {
		if (servicesProviders?.[Services.SALE]?.id !== providerId) {
			// Check if oemVehicles is loaded before accessing it
			if (!oemVehicles || Object.keys(oemVehicles).length === 0) {
				return;
			}

			const canonVehicleId = Object.values(oemVehicles)[0].id;
			const provider = await getServicesProvider(providerId, canonVehicleId);
			updateServicesProviders(Services.SALE, provider);
		}
	}

	async function getAndSetServicesProviders() {
		try {
			// Check if oemVehicles is loaded before accessing it
			if (!oemVehicles || Object.keys(oemVehicles).length === 0) {
				return;
			}

			const canonVehicleId = Object.values(oemVehicles)[0].id;
			const servicesProvidersResponse = await getServicesProviders(canonVehicleId);
			setFetchedServicesProviders(servicesProvidersResponse.servicesProviders);
		} catch (error) {
			console.error('Error fetching services providers:', error);
		}
	}
	async function fetchVehicleConfig(vehicleProductId) {
		const { vehicleConfigData, defaultVehicleConfigData } = await getVehicleConfig(
			vehicleProductId,
		);
		const searchParams = new URLSearchParams(window.location.search);
		const encodedConfig = searchParams.get('config');

		// Set vehicle config first as it's needed for smart configuration
		setVehicleConfig(vehicleConfigData);

		// Always fetch OEM vehicle data as it's needed for both paths
		const { REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY } = process.env;
		const oemId = getOemId(REACT_APP_OEM_KEY, REACT_APP_BRAND_KEY);
		const { vehicles: fetchedOemVehicles } = await getOemVehicles(oemId, [vehicleProductId]);
		const vehiclesArr = Object.values(fetchedOemVehicles);
		const currentOemVehicle = fetchedOemVehicles[vehicleProductId];

		if (encodedConfig) {
			// Use encoded config from URL params if available
			const decodedConfig = JSON.parse(decodeURIComponent(encodedConfig));
			setCurrentVehicleConfig(decodedConfig);
		} else if (
			currentOemVehicle &&
			isVehicleConfigurationValid(vehicleConfigData, currentOemVehicle)
		) {
			// Generate smart configuration with availability data
			const smartConfig = generateSmartConfiguration(vehicleConfigData, currentOemVehicle);
			setCurrentVehicleConfig(smartConfig);
			setDisableCheckout(false);
		} else {
			// Vehicle is out of stock, use default config but disable checkout
			setCurrentVehicleConfig(defaultVehicleConfigData);
			setDisableCheckout(true);
			createLog(
				'fetchVehicleConfig',
				'Vehicle is out of stock, using default config and disabling checkout',
				'INFO',
				{
					vehicleProductId,
					vehicleValid: currentOemVehicle
						? isVehicleConfigurationValid(vehicleConfigData, currentOemVehicle)
						: false,
				},
			);
		}

		// Update contexts with the fetched data
		await getAndSetOemVehicles(vehiclesArr);
		await setVehicles(vehiclesArr);
		await getAndSetRelevantFinancingPrograms(
			vehiclesArr,
			setRelevantFinancingPrograms,
			oemProfileProps,
		);
		// RANDOM STUFF (for now)
		await getAndSetStateData();
		await setActiveModule(MODULES.CONFIGURATOR);
	}

	async function fetchCheckoutData(existingCartId) {
		const searchParams = new URLSearchParams(window.location.search);
		setIsLoading(true);

		await getAndSetStateData();
		await getGoogleMapsApiKey();
		const { REACT_APP_OEM_KEY } = process.env;
		createLog('getAndSetCheckoutCart', `REACT_APP_OEM_KEY: ${REACT_APP_OEM_KEY}`, 'INFO', {});
		if (!(REACT_APP_OEM_KEY in oemMap)) {
			throw new Error('Error: no OEM with provided name!');
		}
		let cartId;
		if (searchParams.get('cartId')) {
			cartId = searchParams.get('cartId');
		}
		// SWITCHING TO CHECKOUT FROM CONFIGURATOR
		if (existingCartId) {
			cartId = existingCartId;
		}
		// get cartId from URL params
		const checkoutCartData = await getCheckoutCart(cartId);
		const {
			id: checkoutCartId,
			nativeDefaultCheckoutUrl,
			registrationState: preSelectedRegistrationState,
			vehicles: vehiclesArr,
			uniqueAccessoriesMap,
			looseAccessoriesMap,
			initAccessoryCounts,
			hasBoltOns,
			discounts,
		} = checkoutCartData;
		if (
			checkoutCartData.orderCreationType === ORDER_CREATION_TYPES.ABANDONEDCART ||
			checkoutCartData.orderCreationType === ORDER_CREATION_TYPES.ABANDONED_ANTE_PREORDER
		) {
			setAbandonedCartId(checkoutCartId);
		}
		// if the 'preview' query param is not true, then call checkoutCartWasOpened to log that the checkout cart was opened
		if (searchParams.get('preview') !== 'true') {
			checkoutCartWasOpened(checkoutCartId);
		}
		if (preSelectedRegistrationState) {
			setRegistrationAddress({
				addressString: '',
				addressLineOne: '',
				addressLineTwo: '',
				city: '',
				county: '',
				state: preSelectedRegistrationState,
				zip: '',
			});
		} else {
			setRegistrationAddress({
				addressString: '',
				addressLineOne: '',
				addressLineTwo: '',
				city: '',
				county: '',
				state: '',
				zip: '',
			});
		}
		// Redirect to default checkout if there are no vehicles
		if (vehiclesArr.length === 0) {
			window.location.href = nativeDefaultCheckoutUrl;
			// Return so that the page doesn't load with no vehicles
			return;
		}
		await getAndSetOemVehicles(vehiclesArr);
		await setCartHasBoltOns(hasBoltOns);
		await setCheckoutCart(checkoutCartData);
		await setVehicles(vehiclesArr);
		await setSelectedVariantOptions(getVariantOptionsFromVehicles(vehiclesArr));
		await setUniqueAccessories(uniqueAccessoriesMap);
		await setLooseAccessories(looseAccessoriesMap);
		await setAccessoryCounts(initAccessoryCounts);
		await getAndSetRelevantFinancingPrograms(
			vehiclesArr,
			setRelevantFinancingPrograms,
			oemProfileProps,
		);
		// if there are discounts, update the activeDiscounts context
		if (discounts?.length > 0) {
			const formattedDiscounts = discounts.map((discount) => {
				return {
					name: discount.name,
					type: discount.type,
					amount: discount.amount,
					applicableTo: discount.applicableTo,
					applicableFor: ['ALL'],
					reid: discount.reid || null,
					campaignId: discount.campaignId || null,
					category: discount.category,
				};
			});
			// only allow a single discount at the moment => return the discount obj with the largest amount
			const singleDiscount = formattedDiscounts.sort((a, b) => b.amount - a.amount)[0];
			await setActiveDiscounts([singleDiscount]);
			await setApplicableDiscounts((prevDiscounts) => {
				// Filter out duplicates by checking campaignId
				const newDiscounts = formattedDiscounts.filter(
					(newDiscount) =>
						!prevDiscounts.some(
							(existingDiscount) => existingDiscount.name === newDiscount.name,
						),
				);
				return [...prevDiscounts, ...newDiscounts];
			});
		}
		await setCheckoutFired(Math.random());
	}

	// parse URL params
	useEffect(() => {
		// manually handle sign ins to avoid situation where we create an auth account and user db obj not yet create
		// this will exclusively be used for signouts
		onAuthStateChanged(auth, async (buyer) => {
			if (!buyer) {
				setRegisteredUser(null);
			}
		});

		if (oemProfileProps === null) {
			getOem();
		}

		if (
			(window.location.pathname === '/checkout' ||
				window.location.pathname === '/PurchaseSuccessPage') &&
			uniqueAccessories === null
		) {
			if (productId) {
				setProductId(productId);
				fetchVehicleConfig(productId);
			} else {
				fetchCheckoutData();
			}
		}
	}, []);

	// this useEffect is used to autoApply applicable discounts from discountCampaigns
	useEffect(() => {
		if (vehicles.length > 0 && oemProfileProps) {
			const { discountCampaigns } = oemProfileProps;
			if (!discountCampaigns) return;
			const liveAutoApplyDiscounts = Object.values(discountCampaigns).filter(
				(discount) =>
					isDiscountLive(discount) &&
					discount.applicableFor &&
					idQualifyForDiscount(vehicles[0].productId, discount),
			); // Only auto apply discountCampaigns that have an applicableFor array
			let formattedDiscounts = [];
			if (liveAutoApplyDiscounts?.length > 0) {
				// add all live auto apply discounts to the applicableDiscounts context before filtering
				setApplicableDiscounts((prevDiscounts) => {
					const newDiscounts = liveAutoApplyDiscounts.filter(
						(newDiscount) =>
							!prevDiscounts.some(
								(existingDiscount) => existingDiscount.name === newDiscount.name,
							),
					);
					return [...prevDiscounts, ...newDiscounts];
				});
				formattedDiscounts = liveAutoApplyDiscounts.map((discount) => ({
					name: discount.name,
					type: discount.type,
					amount: discount.amount,
					startsAt: discount.startsAt,
					endsAt: discount.endsAt,
					applicableTo: discount.applicableTo,
					applicableFor: discount.applicableFor,
					reid: discount.reid || null,
					campaignId: discount.campaignId || null,
					category: DISCOUNT_CATEGORIES.DISCOUNT_CAMPAIGN,
				}));
				if (activeModule !== MODULES.CONFIGURATOR) {
					// if we are in the configurator, we don't need to filter for selectedPaymentOption
					formattedDiscounts = formattedDiscounts.filter((discount) =>
						discount.applicableFor.includes(selectedPaymentOption),
					);
				}
				if (formattedDiscounts.length) {
					// only allow a single discount at the moment => return the discount obj with the largest amount
					const singleDiscount = formattedDiscounts.sort(
						(a, b) => b.amount - a.amount,
					)[0];
					setActiveDiscounts([singleDiscount]);
				} else {
					setActiveDiscounts([]);
				}
			}
		}
	}, [
		vehicles,
		oemProfileProps,
		selectedPaymentOption,
		setActiveDiscounts,
		setApplicableDiscounts,
		activeModule,
		checkoutCart,
		oemVehicles,
	]);

	// Update Sardine whenever emailAddress changes
	useEffect(() => {
		const run = async () => {
			// Only run if email address exists and is in valid format
			if (accountDetails.emailAddress && isValidEmail(accountDetails.emailAddress)) {
				getAndSetSession();
			}
		};
		run();
	}, [accountDetails.emailAddress]);

	useEffect(() => {
		function handleBeforeUnload() {
			track_checkout_buyer_abandoned_cart(
				vehicles,
				uniqueAccessories,
				oemProfileProps,
				checkoutCart,
				abandonedCartId,
				isPurchasePreorder(checkoutCart),
			);
		}

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [vehicles, uniqueAccessories, oemProfileProps, checkoutCart, abandonedCartId]);

	// create paymentIntent when necessary information is available
	useEffect(() => {
		// Write an async function that creates three different payment intents using a promise array and the createCheckoutPaymentIntent function
		async function initPaymentIntent() {
			const hasNoVinnedVehicles = Object.values(oemVehicles).every(
				(vehicle) => !vehicleNeedsVin(vehicle),
			);
			// once we have the oem, create a paymentIntent
			if (isPurchasePreorder(checkoutCart)) {
				const paymentIntentsRes = await Promise.all([
					// PREORDER RESERVATION
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.PREORDER_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.PREORDER_RESERVATION, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.CARD], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
					// VIP PREORDER RESERVATION
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.PREORDER_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.PREORDER_VIP_RESERVATION, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.CARD], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
				]);
				const paymentIntentsFutureValue = {
					[PAYMENT_CHOICES.PREORDER_RESERVATION]: paymentIntentsRes[0],
					[PAYMENT_CHOICES.PREORDER_VIP_RESERVATION]: paymentIntentsRes[1],
				};
				setPaymentIntents(paymentIntentsFutureValue);
				setCurrentPaymentIntent(paymentIntentsFutureValue[selectedPaymentOption]);
			} else {
				const paymentIntentsRes = await Promise.all([
					// PAY IN FULL
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.REMAINING_BALANCE_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.CHECKOUT_FULL_PAYMENT, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.CARD], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
					// FINANCING
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.RESERVATION_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.LOAN_DOWNPAYMENT, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.CARD], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
					// RESERVATION
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.RESERVATION_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.STANDARD_RESERVATION, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.CARD], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
					// KLARNA PAY IN FULL
					createCheckoutPaymentIntent(
						PAYMENT_DESCRIPTION_TYPES.REMAINING_BALANCE_PAYMENT.description, // paymentDescription
						9000, // amountToPay
						PAYMENT_TYPES.KLARNA_CHECKOUT_FULL_PAYMENT, // paymentType
						checkoutCart?.id, // checkoutCartId
						checkoutCart.oemId,
						false, // processingFeeInc
						0, // processingFeeIncAmount
						[STRIPE_PAYMENT_METHOD_TYPES.KLARNA], // paymentMethodTypes
						hasNoVinnedVehicles,
					),
				]);
				const paymentIntentsFutureValue = {
					[PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT]: paymentIntentsRes[0],
					[PAYMENT_CHOICES.LOAN_DOWNPAYMENT]: paymentIntentsRes[1],
					[PAYMENT_CHOICES.STANDARD_RESERVATION]: paymentIntentsRes[2],
					[PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT]: paymentIntentsRes[3],
					[PAYMENT_CHOICES.IN_PERSON_PAYMENT]: paymentIntentsRes[1],
				};
				setPaymentIntents(paymentIntentsFutureValue);
				setCurrentPaymentIntent(paymentIntentsFutureValue[selectedPaymentOption]);
			}
			await setIsLoading(false);
			await setActiveModule(MODULES.PAYMENT);
		}

		// only run if a.) oem has been set and b.) checkoutCart has been unpacked, and c.) paymentIntent hasn't been created yet d.) location is /checkout and e.) payment option has been selected
		const { pathname } = window.location;
		if (
			vehicles.length > 0 &&
			uniqueAccessories !== null &&
			accessoryCounts !== null &&
			currentPaymentIntent === null &&
			pathname === '/checkout' &&
			selectedPaymentOption !== null &&
			activeModule !== MODULES.CONFIGURATOR
		) {
			// if we are not re-using a checkout cart that is associated with an existing order, create a new payment intent
			initPaymentIntent();
		} else if (
			vehicles.length > 0 &&
			uniqueAccessories !== null &&
			accessoryCounts !== null &&
			currentPaymentIntent != null &&
			pathname === '/checkout' &&
			selectedPaymentOption !== null &&
			activeModule !== MODULES.CONFIGURATOR &&
			activeModule !== MODULES.LEAD_GEN_SUCCESS
		) {
			setIsLoading(false);
			setActiveModule(MODULES.PAYMENT);
		}
	}, [
		oemProfileProps,
		vehicles,
		uniqueAccessories,
		accessoryCounts,
		checkoutCart,
		selectedPaymentOption,
		checkoutFired,
		activeModule,
	]);

	// useEffect to call services provider functions once oemVehicles is loaded
	useEffect(() => {
		if (oemVehicles && Object.keys(oemVehicles).length > 0) {
			// Call getAndSetServicesProviders once oemVehicles is available
			getAndSetServicesProviders();

			// Call getAndSetSellingServicesProvider if oemProfileProps is also available
			if (oemProfileProps?.servicesProviders?.sale) {
				getAndSetSellingServicesProvider(oemProfileProps?.servicesProviders?.sale);
			}
		}
	}, [oemVehicles, oemProfileProps]);

	useEffect(() => {
		if (oemProfileProps?.useInventory) {
			const saleProvider = servicesProviders?.[Services.SALE];
			const inventoryProvider = servicesProviders?.[Services.INVENTORY];

			if (saleProvider !== prevSaleProviderRef.current) {
				if (!saleProvider) {
					updateServicesProviders(Services.INVENTORY, null);
				} else if (saleProvider.id !== inventoryProvider?.id) {
					updateServicesProviders(Services.INVENTORY, saleProvider);
				}
			}

			prevSaleProviderRef.current = saleProvider;
		}
	}, [oemProfileProps?.useInventory, servicesProviders]);

	// useEffect to update the currentPaymentIntent whenever the selectedPaymentOption changes
	useEffect(() => {
		if (paymentIntents !== null && selectedPaymentOption) {
			setCurrentPaymentIntent(paymentIntents[selectedPaymentOption]);
			// If payment option is IN_PERSON_PAYMENT, force delivery to PICK_UP
			if (selectedPaymentOption === PAYMENT_CHOICES.IN_PERSON_PAYMENT) {
				setSelectedDeliveryOption(DELIVERY_CHOICES.PICK_UP);
			}
		}
	}, [selectedPaymentOption]);

	// useEffect that runs when OEM and checkoutCart are available to set the default selectedPaymentOption and selectedDeliveryOption and selectedRegistrationOption
	useEffect(() => {
		if (
			relevantFinancingPrograms !== null &&
			oemProfileProps &&
			oemVehicles &&
			checkoutCart &&
			selectedPaymentOption === null &&
			selectedDeliveryOption === null &&
			selectedRegistrationOption === null
		) {
			const checkoutPaymentOverride =
				checkoutCart?.customLinkConfig?.checkoutPaymentOverride ?? null;
			const paymentConfig = getPaymentConfigOptions(
				oemProfileProps,
				Object.values(oemVehicles || {}),
				checkoutPaymentOverride,
				isPurchasePreorder(checkoutCart),
			);
			if (isPurchasePreorder(checkoutCart)) {
				const {
					PREORDER_RESERVATION: oemPreorderReservation,
					PREORDER_VIP_RESERVATION: oemPreorderVipReservation,
				} = paymentConfig;
				// configure ability to pre-select
				let prefilledPaymentOption = null;
				// let prefilledDeliveryOption = null;
				if (checkoutCart.customLinkConfig?.prefilledInfo) {
					const { selectedPaymentOption: tempSelectedPaymentOption } =
						checkoutCart.customLinkConfig.prefilledInfo;
					if (tempSelectedPaymentOption) {
						prefilledPaymentOption = tempSelectedPaymentOption;
					}
				}
				if (prefilledPaymentOption) {
					if (
						PAYMENT_CHOICES.PREORDER_RESERVATION === prefilledPaymentOption &&
						oemPreorderReservation
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.PREORDER_RESERVATION);
					} else if (
						PAYMENT_CHOICES.PREORDER_VIP_RESERVATION === prefilledPaymentOption &&
						oemPreorderVipReservation
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.PREORDER_VIP_RESERVATION);
					} else {
						setSelectedPaymentOption(PAYMENT_CHOICES.PREORDER_RESERVATION);
					}
				} else if (oemPreorderReservation) {
					setSelectedPaymentOption(PAYMENT_CHOICES.PREORDER_RESERVATION);
				} else if (oemPreorderVipReservation) {
					setSelectedPaymentOption(PAYMENT_CHOICES.PREORDER_VIP_RESERVATION);
				}
			} else {
				const {
					CHECKOUT_FULL_PAYMENT: oemCheckoutFullPayment,
					STANDARD_RESERVATION: oemStandardReservation,
					LOAN_DOWNPAYMENT: oemLoanDownpayment,
					KLARNA_CHECKOUT_FULL_PAYMENT: oemKlarnaCheckoutFullPayment,
					IN_PERSON_PAYMENT: oemInPersonPayment,
				} = paymentConfig;
				// const prefilledRegistrationOption = null;
				let prefilledPaymentOption = null;
				// let prefilledDeliveryOption = null;
				if (checkoutCart.customLinkConfig?.prefilledInfo) {
					const { selectedPaymentOption: tempSelectedPaymentOption } =
						checkoutCart.customLinkConfig.prefilledInfo;
					if (tempSelectedPaymentOption) {
						prefilledPaymentOption = tempSelectedPaymentOption;
					}
				}
				// For payment, double check that this OEM supports this payment option before we set this
				if (prefilledPaymentOption) {
					if (
						PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT === prefilledPaymentOption &&
						oemCheckoutFullPayment
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT);
					} else if (
						PAYMENT_CHOICES.STANDARD_RESERVATION === prefilledPaymentOption &&
						oemStandardReservation
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.STANDARD_RESERVATION);
					} else if (
						PAYMENT_CHOICES.LOAN_DOWNPAYMENT === prefilledPaymentOption &&
						oemLoanDownpayment
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.LOAN_DOWNPAYMENT);
					} else if (
						PAYMENT_CHOICES.IN_PERSON_PAYMENT === prefilledPaymentOption &&
						oemInPersonPayment
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.IN_PERSON_PAYMENT);
					} else if (
						PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT === prefilledPaymentOption &&
						oemKlarnaCheckoutFullPayment
					) {
						setSelectedPaymentOption(PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT);
					} else {
						setSelectedPaymentOption(PAYMENT_CHOICES.LOAN_DOWNPAYMENT);
						// throw new Error('No payment options available');
					}
				} else if (
					oemLoanDownpayment &&
					relevantFinancingPrograms &&
					Object.values(relevantFinancingPrograms).length > 0
				) {
					// If no prefilledPaymentOption, but the OEM supports financing, default to financing
					setSelectedPaymentOption(PAYMENT_CHOICES.LOAN_DOWNPAYMENT);
				} else if (
					oemKlarnaCheckoutFullPayment &&
					paymentInvoiceTotals &&
					paymentInvoiceTotals.totalCartCostWithoutFullPaymentDiscount >=
						KLARNA_CHECKOUT_MIN &&
					paymentInvoiceTotals.totalCartCostWithoutFullPaymentDiscount <=
						KLARNA_CHECKOUT_MAX
				) {
					// If no prefilledPaymentOption, and the OEM doesn't support financing, or checkout full payment, or cash reservation, default to Klarna
					setSelectedPaymentOption(PAYMENT_CHOICES.KLARNA_CHECKOUT_FULL_PAYMENT);
				} else if (oemCheckoutFullPayment) {
					// If no prefilledPaymentOption, and the OEM doesn't support financing, default to full payment
					setSelectedPaymentOption(PAYMENT_CHOICES.CHECKOUT_FULL_PAYMENT);
				} else if (oemStandardReservation) {
					// If no prefilledPaymentOption, and the OEM doesn't support financing, or checkout full payment, default to cash reservation
					setSelectedPaymentOption(PAYMENT_CHOICES.STANDARD_RESERVATION);
				} else if (oemInPersonPayment) {
					// If no prefilledPaymentOption, and the OEM doesn't support financing, or checkout full payment, or cash reservation, default to in-person payment
					setSelectedPaymentOption(PAYMENT_CHOICES.IN_PERSON_PAYMENT);
				} else {
					// If no prefilledPaymentOption, and the OEM doesn't support financing, or checkout full payment, or cash reservation, throw an error
					throw new Error('No payment options available');
				}
			}
			const { delivery, pickup } = oemProfileProps.fulfillmentOptions;
			let defaultDeliveryOption;
			if (selectedPaymentOption === PAYMENT_CHOICES.IN_PERSON_PAYMENT) {
				defaultDeliveryOption = DELIVERY_CHOICES.PICK_UP;
			} else if (delivery.isEnabled) {
				defaultDeliveryOption = DELIVERY_CHOICES.SHIP;
			} else if (pickup.isEnabled) {
				defaultDeliveryOption = DELIVERY_CHOICES.PICK_UP;
			} else {
				defaultDeliveryOption = DELIVERY_CHOICES.SHIP;
			}
			setSelectedDeliveryOption(defaultDeliveryOption);
		}
	}, [
		oemProfileProps,
		checkoutCart,
		oemVehicles,
		relevantFinancingPrograms,
		selectedPaymentOption,
	]);

	// any time the selectedPaymentOption changes to something other than IN_PERSON_PAYMENT, set the selectedTradeInOption to NO_TRADE_IN
	useEffect(() => {
		if (selectedPaymentOption !== PAYMENT_CHOICES.IN_PERSON_PAYMENT) {
			setSelectedTradeInOption(TRADE_IN_OPTIONS.NO_TRADE_IN);
		}
	}, [selectedPaymentOption]);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	// UseEffect to handle fetching pickup locations when oemProfileProps becomes available
	useEffect(() => {
		if (oemProfileProps) {
			getAndSetOemPickUpLocations();
		}
	}, [oemProfileProps]);

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = [
		demoPages.login.path,
		demoPages.signUp.path,
		layoutMenu.blank.path,
		layoutMenu.checkout.path,
		layoutMenu.configurator.path,
		dashboardMenu.PurchaseSuccessPage.path,
	];

	// Fetch model year vehicles when allModelYears and vehicleConfig are available
	useEffect(() => {
		if (allModelYears && vehicleConfig && isModelYearSelectionEnabled) {
			fetchModelYearVehicles(
				allModelYears,
				oemProfileProps,
				setModelYearVehicles,
				setModelYearStockStatus,
				setIsLoadingModelYearStock,
				vehicleConfig,
			);
		}
	}, [allModelYears, vehicleConfig, isModelYearSelectionEnabled, oemProfileProps]);

	return (
		<ContextWrapper
			registeredUserValue={registeredUserValue}
			oemProfileValue={oemProfileValue}
			currentPaymentIntentValue={currentPaymentIntentValue}
			vehiclesValue={vehiclesValue}
			uniqueAccessoriesValue={uniqueAccessoriesValue}
			looseAccessoriesValue={looseAccessoriesValue}
			accessoryCountsValue={accessoryCountsValue}
			checkoutCartValue={checkoutCartValue}
			cartHasBoltOns={cartHasBoltOnsValue}
			oemVehiclesValue={oemVehiclesValue}
			stackedValue={stackedValue}
			oemPickUpLocationsValue={oemPickUpLocationsValue}
			paymentInvoiceValue={paymentInvoiceValue}
			paymentInvoiceTotalsValue={paymentInvoiceTotalsValue}
			selectedPaymentOptionValue={selectedPaymentOptionValue}
			selectedDeliveryOptionValue={selectedDeliveryOptionValue}
			selectedRegistrationOptionValue={selectedRegistrationOptionValue}
			registrationAddressValue={registrationAddressValue}
			stateDataValue={stateDataValue}
			paymentIntentsValue={paymentIntentsValue}
			activeDiscountsValue={activeDiscountsValue}
			orderIsBeingPlacedValue={orderIsBeingPlacedValue}
			abandonedCartIdValue={abandonedCartIdValue}
			googleMapsApiKeyValue={googleMapsApiKeyValue}
			selectedVariantOptionsValue={selectedVariantOptionsValue}
			accountDetailsValue={accountDetailsValue}
			currentVehicleConfigValue={currentVehicleConfigValue}
			vehicleConfigValue={vehicleConfigValue}
			fetchCheckoutValue={fetchCheckoutValue}
			sessionValue={sessionValue}
			activeModuleValue={activeModuleValue}
			relevantFinancingProgramsValue={relevantFinancingProgramsValue}
			deliveryTimeValue={deliveryTimeValue}
			shippingAddressSameAsRegistrationAddressValue={
				shippingAddressSameAsRegistrationAddressValue
			}
			fetchedServicesProvidersValue={fetchedServicesProvidersValue}
			servicesProvidersValue={servicesProvidersValue}
			modelYearSelectionProviderValue={modelYearSelectionProviderValue}
			variantImagesValue={variantImagesValue}
			selectedTradeInOptionValue={selectedTradeInOptionValue}>
			<ThemeProvider theme={theme}>
				<TourProvider
					steps={steps}
					styles={styles}
					showNavigation={false}
					showBadge={false}>
					<AppRouter
						fullScreenStatus={fullScreenStatus}
						ref={ref}
						oemProfileProps={oemProfileProps}
						vehicles={vehicles}
						oemVehicles={oemVehicles}
						uniqueAccessories={uniqueAccessories}
						accessoryCounts={accessoryCounts}
						checkoutCart={checkoutCart}
						stateData={stateData}
						selectedDeliveryOption={selectedDeliveryOption}
						selectedPaymentOption={selectedPaymentOption}
						stacked={stacked}
						withOutAsidePages={withOutAsidePages}
						vehicleConfig={vehicleConfig}
						currentVehicleConfig={currentVehicleConfig}
					/>
					<Portal id='portal-notification'>
						<ReactNotifications />
					</Portal>
				</TourProvider>
				{/* </ToastProvider> */}
			</ThemeProvider>
		</ContextWrapper>
	);
};

export default App;
